import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import axiosInstance from 'Services/APIService';

// Define the User interface based on the API response
interface User {
  id: string;
  username: string;
  gender: string;
  phone: string;
  dob: string;
  address: string;
  department: string;
  groups: { group_id: string; label: string }[];
  avatar: string;
  user_details: {
    name: string;
    role: { role_id: string; label: string }[];
    email: string;
  };
  isActive: boolean | null;
  token: string;
  approved_on: string;
  approved_by: string;
}

// Define the state interface for the slice
interface ProfileSettingsState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: ProfileSettingsState = {
  user: null,
  loading: false,
  error: null
};

// Async thunk to fetch user by ID
export const fetchUserById = createAsyncThunk<User, string, { rejectValue: string }>(
  'profileSettings/fetchUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/approvedMembers/get-user-by-id?user_id=${userId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user data');
    }
  }
);

// Create the slice
const profileSettingsSlice = createSlice({
  name: 'profileSettings',
  initialState,
  reducers: {
    clearProfileState: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'An error occurred';
      });
  }
});

// Export actions
export const { clearProfileState } = profileSettingsSlice.actions;

// Export reducer
export default profileSettingsSlice.reducer;

// Export selector for use in components
export const selectProfileSettings = (state: { profileSettings: ProfileSettingsState }) =>
  state.profileSettings;
